import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Bonus } from '@sorare/core/src/components/collections/Bonus';
import { CardsNumber } from '@sorare/core/src/components/collections/CardsNumber';
import { Ranking } from '@sorare/core/src/components/collections/Ranking';
import { RankingRatio } from '@sorare/core/src/components/collections/RankingRatio';
import { Score } from '@sorare/core/src/components/collections/Score';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { CollectionPreviewStats_userCardCollection } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  justify-content: space-around;
`;
const StatsWrapper = styled(Vertical).attrs({ gap: 0 })`
  align-self: stretch;
  margin-top: auto;
`;
const StatsLine = styled.div`
  display: flex;
  justify-content: space-between;
`;

type Props = {
  userCardCollection: CollectionPreviewStats_userCardCollection | null;
  slotsCount: number;
};
export const CollectionPreviewStats = ({
  userCardCollection,
  slotsCount,
}: Props) => {
  const defaults = {
    fulfilledSlotsCount: 0,
    score: 0,
    bonus: 0,
    liveRanking: 0,
    liveRankingRatio: null,
  };
  const { fulfilledSlotsCount, score, bonus, liveRanking, liveRankingRatio } =
    userCardCollection || defaults;
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();

  if (enableMyClubSplit) {
    return (
      <Root>
        <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionCards">
          <Vertical gap={0} center>
            <LabelS bold color="var(--c-nd-500)">
              <FormattedMessage {...glossary.cards} />
            </LabelS>
            <CardsNumber
              ownedCards={fulfilledSlotsCount}
              totalCards={slotsCount}
            />
          </Vertical>
        </OnboardingHighlightWrapper>
        <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionRank">
          <Vertical gap={0} center>
            <LabelS bold color="var(--c-nd-500)">
              <FormattedMessage {...fantasy.rank} />
            </LabelS>

            <RankingRatio liveRankingRatio={liveRankingRatio} />
          </Vertical>
        </OnboardingHighlightWrapper>
        <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionScore">
          <Vertical gap={0} center>
            <LabelS bold color="var(--c-nd-500)">
              <FormattedMessage {...fantasy.score} />
            </LabelS>
            <Score score={score} />
          </Vertical>
        </OnboardingHighlightWrapper>
      </Root>
    );
  }

  return (
    <StatsWrapper>
      <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionCards">
        <StatsLine>
          <FormattedMessage {...glossary.cards} />
          <CardsNumber
            ownedCards={fulfilledSlotsCount}
            totalCards={slotsCount}
          />
        </StatsLine>
      </OnboardingHighlightWrapper>
      <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionRank">
        <StatsLine>
          <FormattedMessage {...fantasy.rank} />
          <Ranking liveRanking={liveRanking} />
        </StatsLine>
      </OnboardingHighlightWrapper>
      <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionScore">
        <StatsLine>
          <FormattedMessage {...fantasy.score} />
          <Score score={score} />
        </StatsLine>
      </OnboardingHighlightWrapper>
      <OnboardingHighlightWrapper highlightName="ManagerProfileCollectionBonus">
        <StatsLine>
          <FormattedMessage {...fantasy.bonus} />
          <Bonus bonus={bonus} />
        </StatsLine>
      </OnboardingHighlightWrapper>
    </StatsWrapper>
  );
};

CollectionPreviewStats.fragments = {
  userCardCollection: gql`
    fragment CollectionPreviewStats_userCardCollection on UserCardCollection {
      id
      fulfilledSlotsCount
      score
      bonus
      liveRanking
      liveRankingRatio
    }
  ` as TypedDocumentNode<CollectionPreviewStats_userCardCollection>,
};
